import { useState } from 'react'
import Dropzone from 'react-dropzone'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import { useHistory } from 'react-router'
import Loading from 'atoms/Loading'
import ErrorPanel from 'molecules/ErrorPanel'
import TradingPartnerSelector from 'common/components/selectors/TradingPartnerSelector'
import { TradingPartner } from 'common/types/kraken-core/TradingPartner'
import { DataModelSchemaExportResponse } from 'services/kraken-core/data_model/types'
import { useImportDataModelSchema } from '../api'

const STEPS = ['Upload', 'Confirm', 'Finish']

export interface DataModelSchemaImportModalProps {
  open: boolean
  onClose: () => void
}

export default function DataModelSchemaImportModal({
  open,
  onClose,
}: DataModelSchemaImportModalProps) {
  const history = useHistory()
  const [currentStep, setCurrentStep] = useState(0)
  const [importedData, setImportedData] = useState<DataModelSchemaExportResponse | null>(null)
  const [owner, setOwner] = useState<TradingPartner | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)

  const {
    mutate: doImport,
    isLoading: isImporting,
    error: importError,
    data: importResult,
    reset: resetMutation,
  } = useImportDataModelSchema()

  const handleClose = () => {
    setCurrentStep(0)
    setImportedData(null)
    setOwner(null)
    setParseError(null)
    resetMutation()
    onClose()
  }

  const handleFileDrop = (files: File[]) => {
    const file = files[0]
    if (!file) return
    setParseError(null)

    const reader = new FileReader()
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result?.toString() || '{}')
        setImportedData(data)
      } catch {
        setParseError('Invalid JSON file. Please upload a valid Data Model Schema export file.')
      }
    }
    reader.readAsText(file)
  }

  const handleImport = () => {
    if (!importedData || !owner) return
    const payload = {
      ...importedData,
      owner_id: owner.id,
    }
    doImport(payload, {
      onSuccess: () => setCurrentStep(2),
      onError: () => setCurrentStep(2),
    })
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Upload a JSON file exported from a Data Model Schema.
            </Typography>
            <Dropzone onDrop={handleFileDrop} accept="application/json" maxFiles={1}>
              {({ getRootProps, getInputProps }) => (
                <Paper
                  {...getRootProps()}
                  variant="outlined"
                  sx={{
                    p: 4,
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: 'grey.50',
                    '&:hover': { backgroundColor: 'grey.100' },
                  }}
                >
                  <input {...getInputProps()} />
                  <Typography variant="body1">
                    {importedData
                      ? `✓ ${importedData.description}`
                      : 'Drag & drop a JSON file here, or click to select'}
                  </Typography>
                </Paper>
              )}
            </Dropzone>
            {parseError && <ErrorPanel error={parseError} />}
          </Box>
        )

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">Review</Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Description:</strong> {importedData?.description}
              </Typography>
              <Typography variant="body2">
                <strong>Engine:</strong> {importedData?.engine}
              </Typography>
              <Typography variant="body2">
                <strong>Resources:</strong> {importedData?.resources?.length ?? 0}
              </Typography>
            </Stack>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Owner <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TradingPartnerSelector
                selected={{ id: owner?.id, label: owner?.name }}
                onSelect={tp => setOwner(tp)}
              />
            </Box>
          </Box>
        )

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isImporting && <Loading className="mt-8 justify-center" />}
            {importError && (
              <ErrorPanel
                error={(importError as any)?.message ?? 'An error occurred during import.'}
              />
            )}
            {importResult?.id && (
              <Alert
                severity="success"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      handleClose()
                      history.push(`/data-model/schemas/${importResult.id}`)
                    }}
                  >
                    View
                  </Button>
                }
              >
                Data Model Schema imported successfully.
              </Alert>
            )}
          </Box>
        )

      default:
        return null
    }
  }

  const renderActions = () => {
    switch (currentStep) {
      case 0:
        return (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" disabled={!importedData} onClick={() => setCurrentStep(1)}>
              Next
            </Button>
          </Stack>
        )
      case 1:
        return (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setCurrentStep(0)}>Back</Button>
            <Button variant="contained" disabled={!owner} onClick={handleImport}>
              Import
            </Button>
          </Stack>
        )
      case 2:
        return (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleClose}>Close</Button>
          </Stack>
        )
      default:
        return null
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Data Model Schema</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
            {STEPS.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {renderStepContent()}
        </Box>
      </DialogContent>
      <DialogActions>{renderActions()}</DialogActions>
    </Dialog>
  )
}
